import { useEffect, useMemo, useState } from "react";
import { useAnimals } from "../../../hooks/useAnimals";
import {
  getDisplayKupeNo,
  getDisplaySiparisNo,
} from "../../../lib/animalLabels";
import {
  getSlaughterOps,
  rehydrateSlaughterOps,
  subscribeSlaughterOps,
  upsertSlaughterOp,
  type SlaughterOpStatus,
} from "../../../lib/slaughterOpsStore";

type ColumnStatus = SlaughterOpStatus;
type ActiveStatus = Exclude<ColumnStatus, "done">;

type StatusLabelMap = Record<ColumnStatus, string>;

interface RowItem {
  animalId: number;
  kupeNo: number;
  kupeNoLabel: string;
  siparisNo: string;
  tur: string;
  toplamHisse: number;
  satilanHisse: number;
  status: ColumnStatus;
  packingZone?: number | null;
  queuedAt?: string;
  startedAt?: string;
  cutAt?: string;
  packedAt?: string;
  doneAt?: string;
  previousStatus?: ActiveStatus;
}

const STATUS_LABELS: StatusLabelMap = {
  queued: "Sıra Bekliyor",
  entered: "Kesime Girdi",
  cut: "Kesim Yapıldı",
  packing: "Parçalama",
  done: "Tamamlananlar",
};

function formatTime(value?: string) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

function toNumber(value: string) {
  const parsed = Number(String(value ?? "").replace(/[^\d]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function getTimeText(row: RowItem) {
  if (row.status === "queued") return formatTime(row.queuedAt);
  if (row.status === "entered") return formatTime(row.startedAt);
  if (row.status === "cut") return formatTime(row.cutAt);
  if (row.status === "packing") return formatTime(row.packedAt);
  return formatTime(row.doneAt);
}

function getRowOrderTime(status: ColumnStatus, row: RowItem) {
  if (status === "entered") return new Date(row.startedAt ?? row.queuedAt ?? 0).getTime();
  if (status === "cut") return new Date(row.cutAt ?? row.startedAt ?? row.queuedAt ?? 0).getTime();
  if (status === "packing") return new Date(row.packedAt ?? row.cutAt ?? row.startedAt ?? row.queuedAt ?? 0).getTime();
  if (status === "done") return new Date(row.doneAt ?? row.packedAt ?? row.cutAt ?? 0).getTime();
  return 0;
}

function sortRowsForColumn(status: ColumnStatus, rows: RowItem[]) {
  if (status === "queued") {
    return [...rows].sort((a, b) => a.kupeNo - b.kupeNo);
  }

  if (status === "done") {
    return [...rows].sort((a, b) => {
      const aTime = getRowOrderTime(status, a);
      const bTime = getRowOrderTime(status, b);
      return bTime - aTime || a.kupeNo - b.kupeNo;
    });
  }

  return [...rows].sort((a, b) => {
    const aTime = getRowOrderTime(status, a);
    const bTime = getRowOrderTime(status, b);
    return aTime - bTime || a.kupeNo - b.kupeNo;
  });
}

function SmallActionButton({
  children,
  onClick,
  variant = "primary",
}: {
  children: React.ReactNode;
  onClick: () => void;
  variant?: "primary" | "secondary" | "success" | "warning";
}) {
  const className =
    variant === "success"
      ? "bg-farm-green text-white border-farm-green"
      : variant === "warning"
      ? "bg-farm-gold text-white border-farm-gold"
      : variant === "secondary"
      ? "bg-white text-farm-brown border-farm-border"
      : "bg-farm-earth text-white border-farm-earth";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-7 px-2.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${className}`}
    >
      {children}
    </button>
  );
}

export default function KesimAlaniPage() {
  const { animals, setAllAnimals } = useAnimals();
  const [version, setVersion] = useState(0);
  const [packingTarget, setPackingTarget] = useState<RowItem | null>(null);

  useEffect(() => {
    void rehydrateSlaughterOps();

    return subscribeSlaughterOps(() => {
      setVersion((value) => value + 1);
    });
  }, []);

  useEffect(() => {
    const ops = getSlaughterOps();
    const existingMap = new Map(ops.map((item) => [item.animalId, item]));

    for (const animal of animals) {
      const found = existingMap.get(animal.id);

      if (animal.kesimeUygun) {
        if (!found) {
          void upsertSlaughterOp({
            animalId: animal.id,
            status: "queued",
            queuedAt: new Date().toISOString(),
            hidden: false,
            packingZone: null,
          });
        } else if (found.hidden) {
          void upsertSlaughterOp({
            ...found,
            hidden: false,
          });
        }
      }

      if (!animal.kesimeUygun && found?.status === "queued") {
        void upsertSlaughterOp({
          ...found,
          hidden: true,
        });
      }
    }
  }, [animals]);

  const rows = useMemo<RowItem[]>(() => {
    const ops = getSlaughterOps();
    const opMap = new Map(ops.map((item) => [item.animalId, item]));

    return animals
      .filter((animal) => {
        const op = opMap.get(animal.id);
        if (!animal.kesimeUygun && !op) return false;
        if (!animal.kesimeUygun && op?.status === "queued" && op?.hidden) return false;
        return true;
      })
      .map((animal) => {
        const op = opMap.get(animal.id);
        const status = (op?.status ?? "queued") as ColumnStatus;

        return {
          animalId: animal.id,
          kupeNo: toNumber(getDisplayKupeNo(animal)),
          kupeNoLabel: getDisplayKupeNo(animal),
          siparisNo: getDisplaySiparisNo(animal),
          tur: animal.tur,
          toplamHisse: animal.toplamHisse,
          satilanHisse: animal.satirlanHisse,
          status,
          packingZone:
            status === "packing" ? Number(op?.packingZone ?? 0) || null : null,
          queuedAt: op?.queuedAt,
          startedAt: op?.startedAt,
          cutAt: op?.cutAt,
          packedAt: op?.packedAt,
          doneAt: op?.doneAt,
          previousStatus: op?.previousStatus as ActiveStatus | undefined,
        };
      })
      .filter((row) => Number.isFinite(row.kupeNo) && row.kupeNo > 0);
  }, [animals, version]);

  const columns = useMemo(() => {
    const statuses: ColumnStatus[] = ["queued", "entered", "cut", "packing", "done"];

    return statuses.map((status) => ({
      status,
      title: STATUS_LABELS[status],
      rows: sortRowsForColumn(
        status,
        rows.filter((row) => row.status === status)
      ),
    }));
  }, [rows]);

  async function setStatus(
    row: RowItem,
    status: ColumnStatus,
    packingZone?: number | null
  ) {
    const now = new Date().toISOString();

    const nextRecord =
      status === "queued"
        ? {
            animalId: row.animalId,
            status,
            hidden: false,
            previousStatus: undefined,
            queuedAt: row.queuedAt ?? now,
            startedAt: undefined,
            cutAt: undefined,
            packedAt: undefined,
            doneAt: undefined,
            packingZone: null,
          }
        : status === "entered"
        ? {
            animalId: row.animalId,
            status,
            hidden: false,
            previousStatus: undefined,
            queuedAt: row.queuedAt ?? now,
            startedAt: row.startedAt ?? now,
            cutAt: undefined,
            packedAt: undefined,
            doneAt: undefined,
            packingZone: null,
          }
        : status === "cut"
        ? {
            animalId: row.animalId,
            status,
            hidden: false,
            previousStatus: undefined,
            queuedAt: row.queuedAt ?? now,
            startedAt: row.startedAt ?? now,
            cutAt: row.cutAt ?? now,
            packedAt: undefined,
            doneAt: undefined,
            packingZone: null,
          }
        : status === "packing"
        ? {
            animalId: row.animalId,
            status,
            hidden: false,
            previousStatus: undefined,
            queuedAt: row.queuedAt ?? now,
            startedAt: row.startedAt ?? now,
            cutAt: row.cutAt ?? now,
            packedAt: row.packedAt ?? now,
            doneAt: undefined,
            packingZone: packingZone ?? row.packingZone ?? 1,
          }
        : {
            animalId: row.animalId,
            status,
            hidden: false,
            previousStatus:
              row.status === "done" ? row.previousStatus ?? "cut" : (row.status as ActiveStatus),
            queuedAt: row.queuedAt ?? now,
            startedAt: row.startedAt ?? now,
            cutAt:
              row.cutAt ??
              (row.status === "cut" || row.status === "packing" || row.status === "done"
                ? now
                : undefined),
            packedAt:
              row.status === "packing" || row.previousStatus === "packing"
                ? row.packedAt ?? now
                : undefined,
            doneAt: now,
            packingZone: row.status === "packing" ? row.packingZone ?? null : null,
          };

    await upsertSlaughterOp(nextRecord);

    setAllAnimals((prev) =>
      prev.map((animal) => {
        if (animal.id !== row.animalId) return animal;

        if (status === "queued" || status === "entered") {
          return {
            ...animal,
            kesimeUygun: true,
            tamamlandi: false,
            durum: animal.durum === "Teslim Edildi" ? animal.durum : "Kesim Bekliyor",
          };
        }

        if (status === "cut" || status === "packing") {
          return {
            ...animal,
            kesimeUygun: true,
            tamamlandi: false,
            durum: animal.durum === "Teslim Edildi" ? animal.durum : "Kesildi",
          };
        }

        return {
          ...animal,
          kesimeUygun: true,
          tamamlandi: true,
          durum: animal.durum === "Teslim Edildi" ? animal.durum : "Kesildi",
        };
      })
    );
  }

  function renderActions(row: RowItem) {
    if (row.status === "queued") {
      return (
        <div className="flex items-center gap-1 flex-wrap justify-end">
          <SmallActionButton onClick={() => void setStatus(row, "entered")}>Başlat</SmallActionButton>
        </div>
      );
    }

    if (row.status === "entered") {
      return (
        <div className="flex items-center gap-1 flex-wrap justify-end">
          <SmallActionButton
            onClick={() => void setStatus(row, "cut")}
            variant="warning"
          >
            Kesim Yapıldı
          </SmallActionButton>
          <SmallActionButton
            onClick={() => void setStatus(row, "queued")}
            variant="secondary"
          >
            Geri Al
          </SmallActionButton>
        </div>
      );
    }

    if (row.status === "cut") {
      return (
        <div className="flex items-center gap-1 flex-wrap justify-end">
          <SmallActionButton onClick={() => setPackingTarget(row)}>
            Parçalama Seç
          </SmallActionButton>
          <SmallActionButton
            onClick={() => void setStatus(row, "done")}
            variant="success"
          >
            Direkt Tamamla
          </SmallActionButton>
          <SmallActionButton
            onClick={() => void setStatus(row, "entered")}
            variant="secondary"
          >
            Geri Al
          </SmallActionButton>
        </div>
      );
    }

    if (row.status === "packing") {
      return (
        <div className="flex items-center gap-1 flex-wrap justify-end">
          <SmallActionButton onClick={() => setPackingTarget(row)} variant="warning">
            Bölüm Değiştir
          </SmallActionButton>
          <SmallActionButton
            onClick={() => void setStatus(row, "done")}
            variant="success"
          >
            Tamamlandı
          </SmallActionButton>
          <SmallActionButton
            onClick={() => void setStatus(row, "cut")}
            variant="secondary"
          >
            Geri Al
          </SmallActionButton>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1 flex-wrap justify-end">
        <SmallActionButton
          onClick={() => void setStatus(row, row.previousStatus ?? "cut")}
          variant="secondary"
        >
          Geri Al
        </SmallActionButton>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="max-w-[1800px] mx-auto px-3 sm:px-5 py-4 space-y-3">
        <div className="bg-white border border-farm-border rounded-farm px-4 py-3">
          <h1 className="font-heading font-bold text-base text-farm-brown">
            Kesim Alanı
          </h1>
          <p className="text-[11px] text-farm-muted mt-1 leading-5">
            Kesim tikine basılan hayvanlar doğrudan bu ekrana düşer.{" "}
            <span className="font-semibold text-farm-brown">
              Sıra Bekliyor kolonu her zaman Küpe No’ya göre sıralanır.
            </span>{" "}
            Başlatılan hayvan bekleyenler sırasından çıkar. Tamamlananlar 5. kolonda birikir.
          </p>
        </div>

        <div className="grid xl:grid-cols-5 lg:grid-cols-3 md:grid-cols-2 gap-3">
          {columns.map((column) => (
            <section
              key={column.status}
              className="bg-white border border-farm-border rounded-farm overflow-hidden"
            >
              <div className="px-3 py-2 border-b border-farm-border bg-farm-cream">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-heading text-xs font-bold text-farm-brown uppercase tracking-wide">
                    {column.title}
                  </p>
                  <span className="inline-flex items-center justify-center min-w-[26px] h-6 px-2 rounded-full bg-white border border-farm-border text-[10px] font-semibold text-farm-muted">
                    {column.rows.length}
                  </span>
                </div>
              </div>

              <div className="p-2 space-y-2 max-h-[72vh] overflow-y-auto">
                {column.rows.length === 0 ? (
                  <div className="py-8 text-center text-xs text-farm-muted">
                    Kayıt yok
                  </div>
                ) : (
                  column.rows.map((row) => (
                    <div
                      key={row.animalId}
                      className="border border-farm-border rounded-xl bg-farm-warm px-2.5 py-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-bold text-farm-brown leading-none">
                              {row.kupeNoLabel}
                            </span>
                            <span className="inline-flex items-center rounded-full border border-farm-border bg-white px-2 py-0.5 text-[10px] text-farm-muted">
                              {row.tur}
                            </span>
                            {row.status === "packing" && row.packingZone && (
                              <span className="inline-flex items-center rounded-full border border-farm-gold/20 bg-farm-gold-pale px-2 py-0.5 text-[10px] font-semibold text-farm-gold">
                                Parçalama {row.packingZone}
                              </span>
                            )}
                          </div>

                          <div className="mt-1 text-[11px] text-farm-muted leading-4">
                            <span className="font-medium">Sipariş:</span> {row.siparisNo}
                          </div>

                          <div className="mt-0.5 text-[11px] text-farm-muted leading-4">
                            {row.satilanHisse}/{row.toplamHisse} hisse
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <div className="text-[10px] text-farm-muted">Zaman</div>
                          <div className="text-[11px] font-semibold text-farm-brown leading-4 mt-0.5">
                            {getTimeText(row)}
                          </div>
                        </div>
                      </div>

                      <div className="mt-2 pt-2 border-t border-farm-border/70">
                        {renderActions(row)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          ))}
        </div>
      </div>

      {packingTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white border border-farm-border shadow-xl">
            <div className="px-4 py-3 border-b border-farm-border">
              <h2 className="font-heading text-sm font-bold text-farm-brown">
                Parçalama Bölümü Seç
              </h2>
              <p className="text-[11px] text-farm-muted mt-1">
                {packingTarget.kupeNoLabel} için parçalama alanını seç.
              </p>
            </div>

            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[1, 2, 3, 4, 5, 6].map((zone) => (
                <button
                  key={zone}
                  type="button"
                  onClick={() => {
                    void setStatus(packingTarget, "packing", zone);
                    setPackingTarget(null);
                  }}
                  className="h-11 rounded-xl border border-farm-border bg-farm-cream text-farm-brown text-sm font-semibold hover:border-farm-earth hover:bg-farm-gold-pale transition-colors"
                >
                  Parçalama {zone}
                </button>
              ))}
            </div>

            <div className="px-4 pb-4 flex justify-end">
              <button
                type="button"
                onClick={() => setPackingTarget(null)}
                className="h-10 px-4 rounded-xl border border-farm-border bg-white text-farm-muted text-sm font-semibold hover:bg-farm-cream"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}